import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useNutritionContext } from '../../hooks/useNutritionContext';
import Header from './Header';
import Sidebar from './Sidebar';
import Footer from './Footer';
import CoachBubble from '../CoachBubble';
import { profileAPI } from '../../services/apiService';

const isProfileComplete = (profile) => {
  if (!profile) return false;
  const age = Number(profile.age);
  const weight = Number(profile.weight);
  const height = Number(profile.height);

  return Boolean(
    profile.name &&
    age >= 15 &&
    weight > 0 &&
    height > 0 &&
    profile.gender &&
    profile.activity_level &&
    profile.goal
  );
};

const Layout = ({ children }) => {
  const { state } = useNutritionContext();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [showProfilePrompt, setShowProfilePrompt] = useState(false);
  const [hasCheckedProfile, setHasCheckedProfile] = useState(false);
  
  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      
      // Close sidebar automatically on mobile when resizing
      if (mobile && sidebarOpen) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [sidebarOpen]);
  
  // Handle theme changes
  useEffect(() => {
    if (state.settings.theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [state.settings.theme]);

  useEffect(() => {
    let cancelled = false;

    const checkProfile = async () => {
      if (location.pathname === '/macro-calculator') {
        setShowProfilePrompt(false);
        setHasCheckedProfile(true);
        return;
      }

      const dismissedThisSession = sessionStorage.getItem('macroProfilePromptDismissed') === 'true';
      if (dismissedThisSession) {
        setShowProfilePrompt(false);
        setHasCheckedProfile(true);
        return;
      }

      try {
        const profile = await profileAPI.get();
        if (!cancelled) {
          setShowProfilePrompt(!isProfileComplete(profile));
          setHasCheckedProfile(true);
        }
      } catch (error) {
        if (!cancelled) {
          setShowProfilePrompt(false);
          setHasCheckedProfile(true);
        }
      }
    };

    checkProfile();

    return () => {
      cancelled = true;
    };
  }, [location.pathname]);
  
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const goToMacroCalculator = () => {
    setShowProfilePrompt(false);
    navigate('/macro-calculator');
  };

  const dismissProfilePrompt = () => {
    sessionStorage.setItem('macroProfilePromptDismissed', 'true');
    setShowProfilePrompt(false);
  };
  
  return (
    <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Sidebar */}
      <Sidebar isOpen={sidebarOpen} isMobile={isMobile} />
      
      {/* Mobile sidebar overlay */}
      {isMobile && sidebarOpen && (
        <div 
          className="fixed inset-0 z-20 bg-black bg-opacity-50 transition-opacity"
          onClick={toggleSidebar}
        />
      )}
      
      {/* Main content area */}
      <div className="flex flex-col flex-1 md:ml-64">
        <Header toggleSidebar={toggleSidebar} />
        
        <main className="flex-1 p-4 md:p-6">
          <div className="mx-auto max-w-7xl">
            {children}
          </div>
        </main>
        
        <Footer />
      </div>

      <CoachBubble />

      {hasCheckedProfile && showProfilePrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-2xl dark:bg-gray-800">
            <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
              <span className="text-lg font-black">M</span>
            </div>
            <h2 className="text-xl font-extrabold text-gray-900 dark:text-white">
              Set up your profile
            </h2>
            <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">
              Your profile powers the macro targets, calorie goals, dashboard progress, and AI coach context. Fill it out once so the app can personalize everything correctly.
            </p>
            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={dismissProfilePrompt}
                className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                Not now
              </button>
              <button
                type="button"
                onClick={goToMacroCalculator}
                className="rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-indigo-700"
              >
                Complete Profile
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;
